import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
} from "@progress/kendo-react-grid";
import ReactDOM from "react-dom";

interface SystemPrompt {
  systemPromptId: number;
  promptType: string;
  persona: string;
  promptContent: string;
  isActive: boolean;
  createdOn: string;
  lastModifiedOn: string;
  version: number;
  description: string;
}

const loadingPanelMarkup = (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading</span>
    <div className="k-loading-image" />
    <div className="k-loading-color" />
  </div>
);

const LoadingPanel = (props: { gridRef: React.RefObject<HTMLDivElement> }) => {
  const { gridRef } = props;
  const gridContent =
    gridRef.current && gridRef.current.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanelMarkup, gridContent)
    : loadingPanelMarkup;
};

const SystemPrompts: React.FC = () => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<SystemPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await callApi("sysadmin/ai/prompts", HttpMethod.GET);
      setPrompts(response.data || []);
    } catch (err: any) {
      console.error("Error fetching system prompts:", err);
      setError(`Error fetching system prompts: ${err.message || err}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreate = () => {
    navigate("/sysadmin/ai/prompts/new");
  };

  const handleEdit = (id: number) => {
    navigate(`/sysadmin/ai/prompts/${id}`);
  };

  const handleDelete = async (id: number) => {
    try {
      await callApi(`sysadmin/ai/prompts/${id}`, HttpMethod.DELETE);
      setDeleteConfirm(null);
      loadData();
    } catch (err: any) {
      console.error("Error deleting prompt:", err);
      setError(`Error deleting prompt: ${err.message || err}`);
    }
  };

  // Custom cell for clickable row
  const ClickableCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    const value = field ? dataItem[field] : "";
    const isInactive = !dataItem.isActive;
    return (
      <td
        onClick={() => handleEdit(dataItem.systemPromptId)}
        style={{
          cursor: "pointer",
          opacity: isInactive ? 0.5 : 1,
        }}
      >
        {value?.toString() || "-"}
      </td>
    );
  };

  // Custom cell for date formatting
  const DateCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    const value = field ? dataItem[field] : null;
    const formatted = value
      ? new Date(value).toLocaleString("en-US", {
          dateStyle: "short",
          timeStyle: "short",
        })
      : "-";
    const isInactive = !dataItem.isActive;
    return (
      <td
        onClick={() => handleEdit(dataItem.systemPromptId)}
        style={{
          cursor: "pointer",
          opacity: isInactive ? 0.5 : 1,
        }}
      >
        {formatted}
      </td>
    );
  };

  // Custom cell for active status
  const ActiveCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    const isActive = dataItem.isActive;
    return (
      <td
        onClick={() => handleEdit(dataItem.systemPromptId)}
        style={{
          cursor: "pointer",
          opacity: !isActive ? 0.5 : 1,
        }}
      >
        <span className={`badge ${isActive ? "bg-success" : "bg-secondary"}`}>
          {isActive ? "Active" : "Inactive"}
        </span>
      </td>
    );
  };

  // Custom cell for truncated description
  const DescriptionCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    const value = field ? dataItem[field] : "";
    const truncated =
      value && value.length > 50
        ? value.substring(0, 50) + "..."
        : value || "-";
    const isInactive = !dataItem.isActive;
    return (
      <td
        onClick={() => handleEdit(dataItem.systemPromptId)}
        style={{
          cursor: "pointer",
          opacity: isInactive ? 0.5 : 1,
        }}
        title={value}
      >
        {truncated}
      </td>
    );
  };

  // Custom cell for actions
  const ActionsCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    const id = dataItem.systemPromptId;
    const isInactive = !dataItem.isActive;
    return (
      <td style={{ opacity: isInactive ? 0.5 : 1 }}>
        <button
          className="btn btn-sm btn-primary me-2"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(id);
          }}
        >
          Edit
        </button>
        {dataItem.isActive && (
          <>
            {deleteConfirm === id ? (
              <>
                <button
                  className="btn btn-sm btn-danger me-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(id);
                  }}
                >
                  Confirm
                </button>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirm(null);
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirm(id);
                }}
              >
                Deactivate
              </button>
            )}
          </>
        )}
      </td>
    );
  };

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">System Prompts</h4>
        <button className="btn btn-primary" onClick={handleCreate}>
          + Create New Prompt
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div ref={gridRef}>
        {isLoading && <LoadingPanel gridRef={gridRef} />}
        <Grid
          data={prompts}
          sortable={true}
          resizable={true}
          style={{ height: "calc(100vh - 200px)" }}
        >
          <Column
            field="systemPromptId"
            title="ID"
            width={60}
            cells={{ data: ClickableCell }}
          />
          <Column
            field="promptType"
            title="Type"
            width={100}
            cells={{ data: ClickableCell }}
          />
          <Column
            field="persona"
            title="Persona"
            width={120}
            cells={{ data: ClickableCell }}
          />
          <Column
            field="description"
            title="Description"
            width={250}
            cells={{ data: DescriptionCell }}
          />
          <Column
            field="version"
            title="Version"
            width={80}
            cells={{ data: ClickableCell }}
          />
          <Column
            field="isActive"
            title="Status"
            width={100}
            cells={{ data: ActiveCell }}
          />
          <Column
            field="lastModifiedOn"
            title="Last Modified"
            width={150}
            cells={{ data: DateCell }}
          />
          <Column title="Actions" width={200} cells={{ data: ActionsCell }} />
        </Grid>
      </div>

      <div className="mt-3">
        <small className="text-muted">
          Click on a row to edit the prompt. Inactive prompts are shown with
          reduced opacity.
        </small>
      </div>
    </div>
  );
};

export default SystemPrompts;
